import { createFileRoute } from "@tanstack/react-router";

import { Textarea } from "@/components/ui/textarea";
import {
  AiConfiguration,
  UpdateAiConfiguration,
} from "@/hooks/services/pps/handlers/ai-configuration-api";
import {
  AiConfigurationPayload,
  AiConfigurationResponse,
} from "@/hooks/services/pps/types/ai-configuration.type";
import { WorkspaceResponse } from "@/hooks/services/pps/types/workspace.type";
import { WorkspaceDetail } from "@/hooks/services/pps/handlers/workspace-api";
import { useGetData, usePutData } from "@/hooks/services/use-query-hooks";

import { useState, useEffect } from "react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { WorkspaceToggle } from "@/components/workspace-toggle";

import { toast } from "sonner";
import { useWorkSpaceStore } from "@/stores/store";
import { Card, CardContent } from "@/components/ui/card";
import { Building, Code, Save } from "lucide-react";

const FormSchema = z.object({
  configuration: z.string().min(10, {
    message: "Configuration must be at least 10 characters.",
  }),
});

export const Route = createFileRoute("/prompt-configuration/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { workspace } = useWorkSpaceStore();
  const workspaceId = workspace?.id || 0;

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
  });

  const { data: workspaceData, refetch: setWorkspaceData } =
    useGetData<WorkspaceResponse>(WorkspaceDetail(workspaceId));
  const { data: aiConfigurationData, refetch: setAiConfigurationData } =
    useGetData<AiConfigurationResponse>(
      AiConfiguration({
        workspaceId,
      }),
    );

  const { mutate } = usePutData<AiConfigurationPayload, any>(
    UpdateAiConfiguration(),
  );

  const [configuration, setConfiguration] = useState(
    aiConfigurationData?.data.configuration,
  );

  useEffect(() => {
    setWorkspaceData();
  }, [workspaceId, setWorkspaceData]);

  useEffect(() => {
    setConfiguration(aiConfigurationData?.data.configuration);
    form.setValue(
      "configuration",
      aiConfigurationData?.data.configuration || "",
    );
  }, [aiConfigurationData, form]);

  function onSubmit(data: z.infer<typeof FormSchema>) {
    mutate(
      {
        workspaceId,
        configuration: data.configuration,
      },
      {
        onSuccess: () => {
          toast("Success.", {
            description: `Prompt configuration updated successfully.`,
            className: "success-sonner ",
            descriptionClassName: "success-sonner-description",
          });
          setAiConfigurationData();
        },
        onError: () => {
          toast("Error.", {
            description:
              "There was an issue updating the prompt configuration. Please try again or contact support if the problem persists",
            className: "destructive-sonner ",
            descriptionClassName: "destructive-sonner-description",
          });
        },
      },
    );
  }

  return (
    <div className="aero:flex aero:flex-col aero:gap-4 aero:w-full aero:max-w-[1104px] aero:mx-auto">
      <div className="aero:flex aero:items-center aero:gap-3">
        <div
          className={
            "aero:w-10 aero:h-10 aero:rounded-lg aero:bg-brand-primary-50 aero:flex aero:items-center aero:justify-center"
          }
        >
          <Code className="aero:text-brand-primary-500 aero:h-[27px] aero:w-[27px] aero:p-0.5" />
        </div>
        <h1 className="aero:text-xl aero:md:text-2xl aero:text-foreground aero:font-bold aero:leading-9">
          AI Prompt Configuration
        </h1>
      </div>

      <Card className="aero:!rounded-lg aero:!border-neutral-300 aero:!p-4 aero:!gap-2 aero:!shadow-none">
        <div className="aero:flex aero:items-center aero:justify-between aero:pb-4 aero:border-b aero:border-neutral-400">
          <div className="aero:flex aero:items-center aero:gap-3">
            <div className="aero:w-10 aero:h-10 aero:rounded-lg aero:bg-brand-primary-50 aero:flex aero:items-center aero:justify-center">
              <Building className="aero:h-5 aero:w-5 aero:text-brand-primary-500" />
            </div>
            <div className="aero:flex aero:flex-col">
              <span className="aero:text-neutral-500 aero:text-xs aero:font-medium">
                Current Workspace:
              </span>
              <h2 className="aero:text-xl aero:font-bold aero:leading-9 aero:text-foreground">
                {workspaceData?.data?.workspaceName || "No workspace selected"}
              </h2>
            </div>
          </div>
          <WorkspaceToggle />
        </div>
        <CardContent className="aero:!p-0 aero:!pt-4">
          <Form {...form}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                form.handleSubmit(onSubmit)(e);
              }}
              className="aero:space-y-6"
            >
              <FormField
                control={form.control}
                name="configuration"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="aero:flex aero:items-center aero:gap-2 aero:!font-bold aero:text-lg aero:leading-7 aero:text-brand-primary-600">
                      <span className="aero:w-[3px] aero:h-7 aero:bg-brand-primary-600 aero:rounded-full" />
                      Prompt Configuration
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Type your message here."
                        id="message"
                        rows={10}
                        className="aero:font-inter aero:font-normal aero:text-sm aero:leading-5 aero:text-foreground aero:placeholder:text-neutral-500"
                        value={configuration}
                        defaultValue={configuration}
                        onChange={(e) => {
                          setConfiguration(e.target.value);
                          field.onChange(e);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="aero:w-full aero:flex aero:justify-end">
                <Button
                  className="aero:w-[105px] aero:h-9 aero:rounded aero:px-3 aero:py-2 aero:gap-2 aero:bg-brand-primary-700 aero:hover:bg-brand-primary-800"
                  intent="primary"
                >
                  <Save className="aero:w-5 aero:h-[13.33px] aero:text-neutral-50" />
                  Submit
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
